// src/shared/changelog/ChangelogPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ReactComponent as IconChangelog } from "@assets/Icons/IconChangelog.svg";
import GenericSearchbar, {
  ParsedFilter,
} from "@ui-components/Searchbar/GenericSearchbar";
import GenericTabs from "@ui-components/Tabs/GenericTabs";
import { useAppSelector } from "@redux/hooks";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { getChangelog } from "./getChangelog";
import { useSeenEntries } from "./useSeenEntries";
import { groupEntriesByMonth } from "./grouping";
import {
  CHANGELOG_PRODUCTS,
  CHANGELOG_PRODUCT_META,
  ChangelogProduct,
  isChangelogProductVisible,
} from "./products";
import { ChangelogEntry } from "./types";
import { formatReleasePeriod } from "./format";
import ChangelogShell from "./components/ChangelogShell";
import MonthGroup from "./components/MonthGroup";

/** Product-filter value meaning "don't filter by product". */
const ALL_PRODUCTS = "all";

/**
 * Apply the searchbar filters to an entry. Keyed filters: `year`, `month`,
 * `name`; a keyless term matches across all of them. Multiple filters are
 * ANDed. Case-insensitive.
 *
 * Product is not one of the keys — it has its own tab row, which is the page's
 * primary affordance for it.
 */
function matchesFilters(
  entry: ChangelogEntry,
  filters: ParsedFilter[],
): boolean {
  const period = formatReleasePeriod(entry.releasedAt).toLowerCase();
  const name = `${entry.title} ${entry.description}`.toLowerCase();

  return filters.every((f) => {
    const val = f.value.trim().toLowerCase();
    if (!val) return true;
    switch (f.key.trim().toLowerCase()) {
      case "year":
        return entry.releasedAt.slice(0, 4).includes(val);
      case "month":
        return `${period} ${entry.releasedAt}`.includes(val);
      case "name":
        return name.includes(val);
      default:
        return `${entry.entryKey} ${period} ${entry.releasedAt} ${name}`.includes(
          val,
        );
    }
  });
}

/**
 * Release Notes (`/changelog`) — a flat feed of entries grouped into months
 * derived from their `releasedAt`. Read-only for every tenant type. Visiting
 * marks every entry it shows as seen, which clears the footer card's tint.
 *
 * Deep-link to a single entry with `/changelog#<entryKey>`.
 */
const ChangelogPage: React.FC = () => {
  const location = useLocation();
  const { markSeen } = useSeenEntries();
  const productList = useAppSelector((state) => state.userProfile.ProductList);

  const axiosClient = useAxiosAuth0();

  const [entries, setEntries] = useState<ChangelogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [filters, setFilters] = useState<ParsedFilter[]>([]);
  const [productFilter, setProductFilter] = useState<string>(ALL_PRODUCTS);

  useEffect(() => {
    let alive = true;
    getChangelog(axiosClient)
      .then((data) => {
        if (alive) setEntries(data);
      })
      .catch(() => {
        // A token failure has already redirected by the time this runs — the
        // axios interceptor handles that case. Anything reaching here is a
        // real fetch failure, and an empty feed would misreport it as "no
        // updates yet", which is the one thing a changelog must not do.
        if (alive) setHasError(true);
      })
      .finally(() => {
        if (alive) setIsLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [axiosClient]);

  /**
   * The page is the archive, so reaching it marks the **whole visible feed**
   * read (§2.2) — not just the newest entry, which is all the old single flag
   * could say. `entries` is what the tenant is entitled to; the product tabs
   * below filter the view, not the visibility.
   *
   * `markSeen` writes nothing and broadcasts nothing when every key is already
   * in the set, so this settles after one pass instead of re-running itself.
   */
  useEffect(() => {
    if (isLoading) return;
    markSeen(entries.map((entry) => entry.entryKey));
  }, [isLoading, entries, markSeen]);

  // Entry targeted by the URL hash (`#platform/2026-07-01-release-notes`).
  const targetEntryKey = location.hash ? location.hash.slice(1) : "";

  /** Catalogue products this tenant is entitled to — the tab row's options. */
  const entitledProducts = useMemo(
    () =>
      CHANGELOG_PRODUCTS.filter((product) =>
        isChangelogProductVisible(product, productList),
      ),
    [productList],
  );

  /**
   * Derived rather than synced: if the tenant switches and the selected product
   * is no longer entitled, the row falls back to "All" instead of silently
   * filtering everything away.
   */
  const activeProduct =
    productFilter === ALL_PRODUCTS ||
    entitledProducts.includes(productFilter as ChangelogProduct)
      ? productFilter
      : ALL_PRODUCTS;

  /**
   * The server already applied the visibility rule, so `entries` IS everything
   * this tenant may see. Only the reader's own choices narrow it further — the
   * product tab and the searchbar — which is why the two empty states below
   * stay distinguishable: one means the feed is empty, the other means the
   * filters are too narrow, and only the second is fixable by the reader.
   */
  const months = useMemo(
    () =>
      groupEntriesByMonth(
        entries.filter(
          (entry) =>
            (activeProduct === ALL_PRODUCTS ||
              entry.products.includes(activeProduct as ChangelogProduct)) &&
            matchesFilters(entry, filters),
        ),
      ),
    [entries, activeProduct, filters],
  );

  /**
   * The jump is instant and deferred a frame, both deliberately.
   *
   * `behavior: "smooth"` is cancelled before it moves anything when the
   * navigation comes from the What's new modal. Measured on that path,
   * `scrollIntoView` runs against the right element and the right scroller
   * and leaves `scrollTop` at 0, while the identical call with
   * `behavior: "auto"` moves it — so the element, the id and the effect deps
   * were never the problem, the animation was. Whatever the unmounting modal
   * does in the same tick (it restores `body.overflow` and returns focus)
   * kills it, and the reader lands at the top of the archive on the one path
   * this deep link exists for. A cold load never hit this because nothing
   * else is running by the time the feed resolves.
   *
   * An instant jump is also what a plain anchor does, and animating a reader
   * through several thousand pixels of archive to reach one entry was never
   * the point of the link.
   *
   * The frame of delay is for the other half: the list must have its layout
   * before there is anything to scroll to.
   */
  useEffect(() => {
    if (!targetEntryKey || isLoading) return;

    const frame = requestAnimationFrame(() => {
      document
        .getElementById(targetEntryKey)
        ?.scrollIntoView({ block: "start" });
    });

    return () => cancelAnimationFrame(frame);
  }, [targetEntryKey, isLoading, months]);

  const header = (
    <div className="flex items-center gap-3">
      <IconChangelog className="h-6 w-6 text-semantic-text-brand" />
      <div>
        <h1 className="text-xl font-semibold text-semantic-text-primary">
          Release Notes
        </h1>
        <p className="text-sm text-semantic-text-secondary">
          What&apos;s new across the platform, newest first.
        </p>
      </div>
    </div>
  );

  const toolbar = (
    <div className="flex flex-col gap-3">
      <GenericSearchbar
        placeholder="Filter by year, month or name (press Enter)"
        suggestedKeys={["year", "month", "name"]}
        inputMaxWidth="100%"
        onFiltersChange={setFilters}
      />
      <GenericTabs
        aria-label="Filter by product"
        tabs={[
          { label: "All", value: ALL_PRODUCTS },
          ...entitledProducts.map((product) => ({
            label: CHANGELOG_PRODUCT_META[product].label,
            value: product,
          })),
        ]}
        activeTab={activeProduct}
        onTabChange={setProductFilter}
        size="xs"
        fullWidth={false}
      />
    </div>
  );

  return (
    <ChangelogShell header={header} toolbar={toolbar}>
      {isLoading ? (
        <p className="py-10 text-center text-sm text-semantic-text-secondary">
          Loading release notes…
        </p>
      ) : hasError ? (
        /* Never fall through to an empty state here: "we couldn't load this"
           and "there is nothing to show" are opposite claims, and a changelog
           that quietly reports the second when the first is true teaches
           people the product stopped shipping. */
        <p className="py-10 text-center text-sm text-semantic-text-secondary">
          We couldn&apos;t load the release notes. Please try again.
        </p>
      ) : entries.length === 0 ? (
        /* Nothing is visible to this tenant at all — a different problem from
           an over-narrow filter, and clearing the filters won't fix it. */
        <p className="py-10 text-center text-sm text-semantic-text-secondary">
          There are no updates for your products yet.
        </p>
      ) : months.length === 0 ? (
        <p className="py-10 text-center text-sm text-semantic-text-secondary">
          No updates match your filters.
        </p>
      ) : (
        <ul className="ml-1.5 flex flex-col gap-6 border-l border-semantic-border-secondary pl-2">
          {months.map((group) => (
            <MonthGroup
              key={group.id}
              group={group}
              targetEntryKey={targetEntryKey}
            />
          ))}
        </ul>
      )}
    </ChangelogShell>
  );
};

export default ChangelogPage;
